import { useCallback } from 'react'
import {
  useNodeTopUsersExportLazyQuery,
  NodeTopUsersExportQuery,
} from '../../../../generated/graphql'
import { useTranslation } from 'react-i18next'
import { useExportData } from '../../../../hooks/export-data.hook'

interface UseExportUsersProps {
  nodeId: string
  nodeName: string
  sortBy?: string
  sort?: string
}

type UserWithCoRank = NodeTopUsersExportQuery['nodeTopUsers']['users'][0]

export function useExportUsers({
  nodeId,
  nodeName,
  sortBy,
  sort,
}: UseExportUsersProps) {
  const { t } = useTranslation('nodes')
  const [fetchUsers] = useNodeTopUsersExportLazyQuery()

  const fetchBatch = useCallback(
    async (offset: number, limit: number) => {
      const { data } = await fetchUsers({
        variables: {
          input: {
            nodeId,
            limit,
            offset,
            sortBy,
            sort,
          },
        },
      })

      if (!data?.nodeTopUsers?.users) {
        return null
      }

      return {
        items: data.nodeTopUsers.users,
        total: data.nodeTopUsers.total,
      }
    },
    [fetchUsers, nodeId, sortBy, sort]
  )

  const getHeaders = () => [
    t('csv-position'),
    t('csv-username'),
    t('csv-corank'),
  ]

  const mapRow = useCallback(
    (userWithCoRank: UserWithCoRank) => [
      userWithCoRank.dbPosition.toString(),
      userWithCoRank.user.username,
      userWithCoRank.coRank.toFixed(2),
    ],
    []
  )

  const { exportData, isExporting, progress } = useExportData({
    fetchBatch,
    getHeaders,
    mapRow,
    filename: `${nodeName}-users`,
  })

  return {
    exportUsers: exportData,
    isExporting,
    progress,
  }
}
